import { Injectable, inject } from '@angular/core';
import { HotToastService } from '@ngxpert/hot-toast';

import { TranslationService } from './translation.service';

type ToastType = 'success' | 'info' | 'warning' | 'error';

@Injectable({
    providedIn: 'root'
})
export class NotificationService {
    private readonly toastService = inject(HotToastService);
    private readonly translationService = inject(TranslationService);

    /**
     * Displays an alert.
     * @param message Message to be displayed to the user.
     * @param [title] Message's title.
     * @param [isTranslated] Whether the message/title combo is translated or not.
     */
    showAlert(message: string, title?: string, isTranslated = false): void {
        if (!isTranslated) {
            this.translationService.getText(message).subscribe(v => (message = v));

            if (!!title) {
                this.translationService.getText(title).subscribe(v => (title = v));
            }
        }

        this.show('warning', message, title);
    }

    /**
     * Displays an information.
     * @param message Message to be displayed to the user.
     * @param [title] Message's title.
     * @param [isTranslated] Whether the message/title combo is translated or not.
     */
    showInfo(message: string, title?: string, isTranslated = false): void {
        if (!isTranslated) {
            this.translationService.getText(message).subscribe(v => (message = v));

            if (!!title) {
                this.translationService.getText(title).subscribe(v => (title = v));
            }
        }

        this.show('info', message, title);
    }

    /**
     * Displays an error.
     * @param message Message to be displayed to the user.
     * @param [title] Message's title.
     * @param [isTranslated] Whether the message/title combo is translated or not.
     */
    showError(message: string, title?: string, isTranslated = false): void {
        if (!isTranslated) {
            this.translationService.getText(message).subscribe(v => (message = v));

            if (!!title) {
                this.translationService.getText(title).subscribe(v => (title = v));
            }
        }

        this.show('error', message, title);
    }

    /**
     * Displays a success message.
     * @param message Message to be displayed to the user.
     * @param [title] Message's title.
     * @param [isTranslated] Whether the message/title combo is translated or not.
     * @param [callback] Callback executed if the user clicks on the button.
     */
    showSuccess(message: string, title?: string, callback?: any, isTranslated = false): void {
        if (!isTranslated) {
            this.translationService.getText(message).subscribe(v => (message = v));

            if (!!title) {
                this.translationService.getText(title).subscribe(v => (title = v));
            }
        }

        const toast = this.show('success', message, title);

        if (!!callback) {
            toast.afterClosed.subscribe(callback);
        }
    }

    private show(type: ToastType, message: string, title?: string): ReturnType<HotToastService['success']> {
        const content = !!title ? `${title}\n${message}` : message;

        return this.toastService[type](content, { className: `sl-toast sl-toast-${type}` });
    }
}
